import React from "react";
import PageShell from "@/components/PageShell";
import { Droplets, Wind, Sun, AlertTriangle } from "lucide-react";

const RULES = [
    {
        Icon: Droplets,
        title: "Wash Cold",
        body: "Wash inside out at 30°C with similar colours. Heavy fleece may bleed on first wash — wash separately the first time.",
    },
    {
        Icon: Wind,
        title: "Air Dry",
        body: "Lay flat on a towel to dry. Hanging stretches the shoulders. Avoid direct sun on dark colours.",
    },
    {
        Icon: Sun,
        title: "No Tumble Dryer",
        body: "Heavyweight fleece and knitwear will shrink and felt in a dryer. Do not risk it.",
    },
    {
        Icon: AlertTriangle,
        title: "No Bleach",
        body: "Even on whites. Use a colour-safe oxygen brightener if needed.",
    },
];

const FABRICS = [
    { name: "Heavyweight Fleece (500gsm)", body: "Cold wash, air dry, fold to store. Re-shape while damp." },
    { name: "Selvedge Denim (14oz)", body: "Wear hard for 6 months. First wash cold, inside out, no detergent. Hang dry." },
    { name: "Merino Knit", body: "Hand wash cold or wool cycle. Lay flat to dry. Never wring." },
    { name: "Ripstop & Tech", body: "Machine wash cold. Zip all pockets. Air dry only. No fabric softener." },
    { name: "Waxed Canvas", body: "Spot clean with cold water and a soft brush. Re-wax annually." },
    { name: "Leather Footwear", body: "Wipe with a damp cloth. Condition every 3 months. Use cedar trees to hold shape." },
];

const Care = () => {
    return (
        <PageShell
            kicker="[Maintenance Manual]"
            title="Care &"
            accent="Repair."
            intro="UKnitwear pieces are designed to outlive trends. Treat them right and they'll be in your rotation for years."
            testid="care-page"
        >
            <section className="border-b-4 border-ink">
                <div className="px-4 md:px-8 py-10 border-b-2 border-ink">
                    <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                        // 01 / The Four Rules
                    </span>
                    <h2 className="font-heading text-4xl md:text-5xl uppercase leading-none mt-1">
                        Don&apos;t Break These.
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                    {RULES.map((r, i) => (
                        <div
                            key={r.title}
                            className={`p-6 md:p-8 ${
                                i !== 3 ? "border-b-2 md:border-b-0 lg:border-r-2" : ""
                            } ${i % 2 !== 0 ? "md:border-l-2" : ""} ${
                                i >= 2 ? "md:border-t-2 lg:border-t-0" : ""
                            } border-ink`}
                            data-testid={`care-rule-${i}`}
                        >
                            <r.Icon className="w-8 h-8 text-hazard" />
                            <h3 className="mt-4 font-heading text-2xl uppercase">{r.title}</h3>
                            <p className="mt-2 text-sm">{r.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="px-4 md:px-8 py-10 border-b-4 border-ink">
                <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-muted-foreground">
                    // 02 / Fabric Specific
                </span>
                <h2 className="font-heading text-4xl md:text-5xl uppercase leading-none mt-1 mb-6">
                    By Material
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-ink">
                    {FABRICS.map((f, i) => (
                        <div
                            key={f.name}
                            className={`p-5 ${
                                i % 2 === 0 ? "md:border-r-2" : ""
                            } ${i < FABRICS.length - 2 ? "border-b-2" : "md:border-b-0"} border-ink`}
                            data-testid={`care-fabric-${i}`}
                        >
                            <h4 className="font-heading text-xl uppercase">{f.name}</h4>
                            <p className="mt-1 text-sm text-foreground/80">{f.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="px-4 md:px-8 py-16 bg-ink text-black text-center border-b-4 border-ink">
                <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-hazard">
                    // 03 / Repair, Don&apos;t Replace
                </span>
                <h2 className="font-heading text-4xl md:text-6xl uppercase leading-[0.9] mt-3 max-w-3xl mx-auto">
                    We patch, darn and re-stitch UKnitwear pieces for life.
                </h2>
                <p className="mt-4 max-w-xl mx-auto text-sm md:text-base text-black/80">
                    Send anything torn, frayed, or unravelled to our studio. We&apos;ll
                    repair it. Cost: shipping only.
                </p>
            </section>
        </PageShell>
    );
};

export default Care;
